<script setup lang="ts">
import {computed, ref, watch} from "vue";
import DosageTakeForm from "@/dosage/components/DosageTakeForm.vue";
import {Dosage} from "@/dosage/interfaces/Dosage";
import DosageInstructionForm from "@/dosage/components/DosageInstructionForm.vue";
import DosageFrequencyForm from "@/dosage/components/DosageFrequencyForm.vue";
import DosageDurationForm from "@/dosage/components/DosageDurationForm.vue";
import {DosageFormData} from "@/dosage/interfaces/DosageForm";
import DosageValidationService, {ValidationError} from "@/dosage/services/DosageValidationService";
import {ProductSummary} from "@/product/interfaces/ProductSummary";
import DosageFormService from "@/dosage/services/DosageFormService";

const props = defineProps<{
    product: Pick<ProductSummary, 'id' | 'name'>;
    lastPrescriptionDate?: Date | null;
    hideDuration?: boolean;
    hideCustomFrequency?: boolean;
    externalErrors?: ValidationError[];
}>();

const emits = defineEmits<{
    (e: 'validation-error', errors: ValidationError[]): void;
}>();

const data = defineModel<DosageFormData>({
    default: DosageFormService.defaultFormValue()
});

const submitted = ref(false);
const internalErrors = ref<ValidationError[]>([]);

/**
 * Get the active errors to display, prioritizing external errors if provided.
 */
const activeErrors = computed<ValidationError[]>(() =>
    props.externalErrors ?? internalErrors.value
);

watch(() => props.lastPrescriptionDate, (date) => {
    if (date) {
        data.value.start = date;
    }
}, {immediate: true});

/**
 * When submitted, re-validate on any data change to keep errors up to date.
 */
watch(data, () => {
    if (!submitted.value) {
        return;
    }

    internalErrors.value = DosageValidationService.validate(data.value, props.product.id, !props.hideDuration).errors;
}, {deep: true});

const validation = computed(() =>
    DosageValidationService.validate(data.value, props.product.id)
);

/**
 * Make the form as dirty and trigger validation errors if any.
 */
const validate = (): boolean => {
    submitted.value = true;
    const result = DosageValidationService.validate(data.value, props.product.id, !props.hideDuration);
    internalErrors.value = result.errors;

    if (!result.isValid) {
        emits('validation-error', result.errors);
    }

    return result.isValid;
};

const fieldError = (field: string): string | null => DosageValidationService.getFieldError(activeErrors.value, field);

const prefill = (dosage: Dosage) => {
    const dose = dosage.dose ?? null;
    const instruction = dosage.instruction?.id ? dosage.instruction : null;
    const frequency = dosage.frequency?.id ? dosage.frequency : null;
    const times = dosage.times?.map(({id: _id, ...rest}) => ({...rest})) ?? [];

    data.value = {
        ...data.value,
        times,
        dose,
        instruction,
        frequency,
    };
};

defineExpose({prefill, validate, validation});
</script>

<template>
  <div class="space-y-4">
    <DosageTakeForm
      v-model:model-value="data"
      :product="product"
      :dose-error="fieldError('dose')"
      :times-error="fieldError('times')"
    />
    <DosageFrequencyForm
      v-model:frequency="data.frequency"
      v-model:dates="data.dates"
      :hide-custom-frequency="hideCustomFrequency"
      :frequency-error="fieldError('frequency')"
      :dates-error="fieldError('dates')"
    />
    <DosageInstructionForm v-model:model-value="data.instruction" />
    <DosageDurationForm
      v-if="!data.dates?.length && !hideDuration"
      v-model:start="data.start"
      v-model:end="data.end"
      :start-error="fieldError('start')"
      :end-error="fieldError('end')"
    />
  </div>
</template>
